import { Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/i18n';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { isReorganizingDomOrderAtom } from '../../atoms/session-atoms';
import { BiCtaNames, BiModalTypes } from '../../bi/types';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useAnalyzeFullSiteQuery } from '../../queries/use-analyze-full-site-query';
import { buildStudioDialogProps } from '../modals/build-studio-dialog-props';
export const useDomOrderRecoveryModal = () => {
  const {
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const studioAdapter = a11yWizardAdapter;
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const setIsReorganizingDomOrder = useSetAtom(isReorganizingDomOrderAtom);
  const {
    refetch: analyzeFullSite
  } = useAnalyzeFullSiteQuery();
  const modalType = BiModalTypes.ContinueFromWhereYouLeft;
  const showDomOrderRecoveryModal = useCallback(() => {
    return new Promise(async resolve => {
      setIsReorganizingDomOrder(true);
      biLogger.modalView({
        modalType
      });
      const shouldRevert = await studioAdapter.getShouldRevertDomReorderOnRecovery();
      const modalProps = {
        ...getRecoveryModalProps(t, shouldRevert),
        onCloseButtonClick: async () => {
          biLogger.modalCtaClick({
            ctaName: BiCtaNames.X,
            modalType
          });
          setIsReorganizingDomOrder(false);
        },
        primaryButtonOnClick: async () => {
          biLogger.modalCtaClick({
            ctaName: BiCtaNames.Continue,
            modalType
          });
          try {
            await studioAdapter.continueReorganizeAllPagesDOMOrder();
            await studioAdapter.enableSiteAutoDOMOrder();
            await analyzeFullSite();
          } catch (e) {
            setIsReorganizingDomOrder(false);
          }
          resolve();
        },
        secondaryButtonOnClick: async () => {
          biLogger.modalCtaClick({
            ctaName: shouldRevert ? BiCtaNames.CancelAndRevertChanges : BiCtaNames.Cancel,
            modalType
          });
          if (shouldRevert) {
            await studioAdapter.revertDomReorder();
            setIsReorganizingDomOrder(false);
          } else {
            await studioAdapter.pauseDomReorderInProgress();
            await analyzeFullSite();
          }
        }
      };
      await a11yWizardAdapter.showDialog(...buildStudioDialogProps(modalProps));
    });
  }, [setIsReorganizingDomOrder, biLogger, modalType, studioAdapter, t, a11yWizardAdapter, analyzeFullSite]);
  return {
    showDomOrderRecoveryModal
  };
};
export const getRecoveryModalProps = (t, shouldRevertDomReorderOnRecovery) => ({
  content: /*#__PURE__*/React.createElement(Text, null, t('accessibility-wizard.footer.dom-order.confirmation-modal.content')),
  primaryButtonText: t('accessibility-wizard.footer.dom-order.recovery-modal.primary-button'),
  secondaryButtonText: shouldRevertDomReorderOnRecovery ? t('accessibility-wizard.footer.dom-order.recovery-modal.secondary-button.revert') : t('accessibility-wizard.footer.dom-order.recovery-modal.secondary-button.cancel'),
  theme: 'standard',
  title: t('accessibility-wizard.footer.dom-order.recovery-modal.title')
});
//# sourceMappingURL=dom-order-recovery-modal.js.map